import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

// Province-scoped crawl URL manager (Admin). Backed by /api/scrape-targets (auth-gated).
type ScrapeTarget = {
  id: string;
  url: string;
  province: string;
  label?: string | null;
  enabled: boolean;
  lastCrawledAt?: string | null;
};

const PROVINCES = [
  'British Columbia', 'Alberta', 'Saskatchewan', 'Manitoba', 'Ontario', 'Quebec',
  'New Brunswick', 'Nova Scotia', 'Prince Edward Island', 'Newfoundland and Labrador',
  'Yukon', 'Northwest Territories', 'Nunavut',
];

const inputCls =
  'w-full bg-[var(--surface)] border border-border rounded-[10px] px-3 py-2 text-[14px] text-[color:var(--text-primary)] outline-none focus:border-[var(--accent)]';
const btnPrimary =
  'px-4 py-2 rounded-[10px] text-[14px] font-medium cursor-pointer transition-colors bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed';
const btnGhost =
  'px-3 py-1.5 rounded-[8px] text-[13px] font-medium cursor-pointer transition-colors bg-[var(--surface-hover)] text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)]';

const ScrapeTargetManager: React.FC = () => {
  const [province, setProvince] = useState('British Columbia');
  const [targets, setTargets] = useState<ScrapeTarget[]>([]);
  const [loading, setLoading] = useState(false);
  const [bulk, setBulk] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  const load = useCallback(async (prov: string) => {
    setLoading(true);
    try {
      const r = await axios.get(`/api/scrape-targets?province=${encodeURIComponent(prov)}`);
      setTargets(r.data);
    } catch {
      setTargets([]);
      setStatus('Failed to load (are you logged in?)');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(province); }, [province, load]);

  const addBulk = async () => {
    if (!bulk.trim()) return;
    setBusy(true); setStatus(null);
    try {
      const r = await axios.post('/api/scrape-targets/bulk', { province, text: bulk });
      const { added, duplicatesOrExisting, invalid } = r.data;
      setStatus(`Added ${added} · skipped ${duplicatesOrExisting} existing · ${invalid} invalid`);
      setBulk('');
      await load(province);
    } catch {
      setStatus('Bulk add failed.');
    } finally { setBusy(false); }
  };

  const toggle = async (t: ScrapeTarget) => {
    setTargets((prev) => prev.map((x) => (x.id === t.id ? { ...x, enabled: !x.enabled } : x)));
    try { await axios.patch(`/api/scrape-targets/${t.id}`, { enabled: !t.enabled }); }
    catch { load(province); }
  };

  const remove = async (t: ScrapeTarget) => {
    if (!window.confirm(`Remove ${t.label || t.url}?`)) return;
    setTargets((prev) => prev.filter((x) => x.id !== t.id));
    try { await axios.delete(`/api/scrape-targets/${t.id}`); }
    catch { load(province); }
  };

  const enabledCount = targets.filter((t) => t.enabled).length;

  return (
    <div>
      <p className="text-[13px] text-[color:var(--text-secondary)] mb-4">
        Municipal sites the ingestion crawler visits, grouped by province. Disabled URLs are skipped.
      </p>

      <div className="flex items-center gap-3 mb-4">
        <label className="flex flex-col gap-1">
          <span className="text-[13px] font-medium text-[color:var(--text-secondary)]">Province</span>
          <select
            value={province}
            onChange={(e) => setProvince(e.target.value)}
            className={inputCls + ' min-w-[240px]'}
          >
            {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>
        <span className="self-end pb-2 text-[13px] text-[color:var(--text-muted)]">
          {targets.length} URLs · {enabledCount} enabled
        </span>
      </div>

      {/* Bulk paste */}
      <label className="flex flex-col gap-1 mb-2">
        <span className="text-[13px] font-medium text-[color:var(--text-secondary)]">Bulk add (one URL per line)</span>
        <textarea
          value={bulk}
          onChange={(e) => setBulk(e.target.value)}
          rows={4}
          placeholder={'https://www.example.ca\nexample2.ca'}
          className={inputCls + ' font-mono text-[13px] resize-y'}
        />
      </label>
      <div className="flex items-center gap-3 mb-6">
        <button type="button" onClick={addBulk} disabled={busy || !bulk.trim()} className={btnPrimary}>
          {busy ? 'Adding…' : `Add to ${province}`}
        </button>
        {status && <span className="text-[13px] text-[color:var(--text-secondary)]">{status}</span>}
      </div>

      {/* List */}
      {loading ? (
        <p className="text-[color:var(--text-secondary)]">Loading…</p>
      ) : targets.length === 0 ? (
        <p className="text-[color:var(--text-muted)]">No URLs for {province} yet.</p>
      ) : (
        <div className="flex flex-col gap-1.5 max-h-[460px] overflow-y-auto pr-1">
          {targets.map((t) => (
            <div
              key={t.id}
              className="flex items-center gap-3 rounded-[10px] bg-card border border-border px-3 py-2"
              style={{ opacity: t.enabled ? 1 : 0.5 }}
            >
              <input type="checkbox" checked={t.enabled} onChange={() => toggle(t)} title="Enabled" className="cursor-pointer" />
              <div className="flex-1 min-w-0">
                <div className="text-[14px] font-medium text-[color:var(--text-primary)] truncate">{t.label || t.url}</div>
                <a href={t.url} target="_blank" rel="noopener noreferrer" className="text-[12px] text-[color:var(--text-muted)] truncate hover:text-[color:var(--accent)]">{t.url}</a>
              </div>
              <button type="button" onClick={() => remove(t)} className={btnGhost}>Remove</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ScrapeTargetManager;
